import { MouseEvent, useEffect, useState } from "react";

import styles from "./Header.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "../icons/ArrowBack";
import LogoIcon from "../icons/Logo";
import FilterIcon from "../icons/Filters";
import useWindowSize from "../../hooks/useWindowSize";
import { ROUTES } from "../../constants/routes";
import { FilterLayout } from "./FilterLayout";
import { BackButtonLayout } from "./ArrowBackLayout";
import { LoginButtonLayout } from "./LoginButtonLayout";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../store/store";
import { setLastVisitedId } from "../../store/slices/currentItem.slice";
import { LocalizationLayout } from "./LocalizationLayout";
import headerAvatar from "../../static/imgs/headerAvatar.png";
import { useTranslation } from "react-i18next";
import LanguageDropDown from "./LanguageDropDown";
import AccountDropDown from "./AccountDropDown";
import CommonCloseModule from "./CommonCloseModule";
import { getUserImageUrl } from "../../utils/imageUrl";

interface IProps {
  className?: string;
  backButtonClassName?: string;
  source?: string;
  clickHandler?: any;
}

const Header = ({ className, backButtonClassName, source, clickHandler }: IProps) => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const token = localStorage.getItem("accessToken");

  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const isFiltersRoute = source === ROUTES.FILTERS;
  const isChatTestRoute = source === ROUTES.CHAT_TEST;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentId = useAppSelector((state: any) => state.item.currentVideo);
  const genres = useAppSelector((state: any) => state.filters.genres);
  const period = useAppSelector((state: any) => state.filters.period);
  const firstName = useAppSelector((state: any) => state.auth.userData.username);
  const userAvatar = useAppSelector((state: any) => state.auth.userData.picture_url);
  const userAvatarUrl = getUserImageUrl(userAvatar);
  const maxYear = useAppSelector((state: any) => state.filters.maxYear);

  const [languageDrop, setLanguageDrop] = useState(false);
  const [accDrop, setAccDrop] = useState(false);
  const [loggedInFlag, setLoggedInFlag] = useState(token);

  const languageClickHandler = () => {
    if (accDrop) {
      setAccDrop(false);
    }
    setLanguageDrop(!languageDrop);
  };

  const languageModalLeaveHandler = () => {
    setLanguageDrop(false);
  };

  const accClickHandler = () => {
    if (languageDrop) {
      setLanguageDrop(false);
    }
    setAccDrop(!accDrop);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
  };

  const filterCounterHandler = (gen: number[], per: number[]) => {
    let counter = 0;
    if (per[0] !== 1980 || per[1] !== maxYear) {
      counter += 1;
    }

    counter += gen.length;

    return counter;
  };

  const refreshHandler = () => {
    window.location.href = ROUTES.FOR_YOU;
  };

  const goToPage = (path: string) => {
    navigate(path);
  };

  const backClickHandler = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const historyIndex = window.history.state?.idx;

    if (typeof historyIndex === "number" && historyIndex > 0) {
      navigate(-1);
      return;
    }

    navigate(ROUTES.FOR_YOU, { replace: true });
  };

  const focusHandler = () => {
    const chooseWrapper = document.getElementById("choose_section");

    if (chooseWrapper) {
      chooseWrapper.focus();
    }
  };

  const renderCloseModule = () => <CommonCloseModule />;

  const renderFilterButton = () => {
    const counter = filterCounterHandler(genres, period);
    const buttonClass = isMobile ? styles.mobileFilterButton : styles.desktopFilterButton;

    return (
      <div
        onClick={() => {
          goToPage(ROUTES.FILTERS);
          dispatch(setLastVisitedId(currentId));
        }}
        data-tour="filters"
        className={buttonClass}
      >
        {isMobile && counter !== 0 && <div className={styles.mobileHeaderButtonCounter}>{counter}</div>}
        <FilterIcon isMobile={isMobile} />
      </div>
    );
  };

  useEffect(() => {
    setLoggedInFlag(token);
  }, [token]);

  useEffect(() => {
    if (pathname === ROUTES.INDEX) {
      window.location.href = ROUTES.FOR_YOU;
    }
  }, [pathname]);

  const dirData = i18n.language === "ar" ? "rtl" : "ltr";

  return (
    <div data-app-header className={classnames(styles.wrapperMobile, className)} onClick={focusHandler}>
      <BackButtonLayout source={source}>
        <div
          dir={dirData}
          className={classnames(
            styles.backButton,
            backButtonClassName,
            windowSize === "mobile" ? styles.mobileBack : "",
          )}
          onClick={backClickHandler}
        >
          <ArrowBackIcon isMobile={windowSize === "mobile"} />
        </div>
      </BackButtonLayout>
      {source !== "hero" && (
        <div className={styles.logoWrapperMobile} onClick={refreshHandler}>
          <LogoIcon />
        </div>
      )}
      <div className={styles.headerButtonsWrapper}>
        {windowSize !== "mobile" && (
          <LocalizationLayout>
            <div
              onClick={languageClickHandler}
              className={`${styles.localizationButton} ${source === "hero" ? styles.heroLocalizationButton : ""} ${languageDrop ? styles.localizationButtonActive : ""}`}
            >
              {i18n.language === "uk" && "UA"}
              {i18n.language === "ru" && "RU"}
              {i18n.language === "en" && "EN"}
              {i18n.language === "ar" && "AR"}
              {i18n.language === "fr" && "FR"}
              {i18n.language === "es" && "ES"}
            </div>
            <LanguageDropDown
              changeLanguage={changeLanguage}
              languageClickHandler={languageClickHandler}
              leaveModalHandler={languageModalLeaveHandler}
              openedFlag={languageDrop}
            />
          </LocalizationLayout>
        )}
        <LoginButtonLayout isMobile={isMobile}>
          {loggedInFlag ? (
            <>
              <div data-tour="profile" onClick={accClickHandler} className={`${styles.headerProfile}`}>
                {accDrop && <div className={styles.activeHeaderProfile} />}
                <img src={userAvatarUrl || headerAvatar} alt="avatar" />
                {!userAvatarUrl && <span>{(firstName || "U")[0]}</span>}
              </div>
              <AccountDropDown openDropHandler={() => setAccDrop(false)} goToPage={goToPage} opened={accDrop} />
            </>
          ) : (
            <div
              dir={dirData}
              onClick={() => goToPage(ROUTES.LOGIN)}
              className={`${styles.logInButton} ${source === "hero" ? styles.heroLogInButton : ""}`}
            >
              <span>{t("logInButton")}</span>
            </div>
          )}
        </LoginButtonLayout>
        <FilterLayout source={source} isMobile={isMobile}>
          {isFiltersRoute ? renderCloseModule() : renderFilterButton()}
        </FilterLayout>

        {isChatTestRoute && renderCloseModule()}
      </div>
    </div>
  );
};

export default Header;
